/**
 * Building a bulk send from an uploaded CSV, in the shape the production comms
 * app sends: one request whose `identities` and `others` are columns (arrays
 * aligned by row), preceded by a bulk log with the same `event_id`.
 */
import { INDIA_ZONE, inZone } from './sendSchedule'

const clean = (value) => String(value ?? '').trim()

/** Event types A to Z, ignoring case (the backend answers them in no order). */
export const sortEventTypes = (eventTypes = []) =>
    [...(eventTypes ?? [])]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

/**
 * Columns an email template may use for its calendar invite. Optional: sent as
 * variables when present (`SendBulkComms.tsx` in the old app).
 */
export const EMAIL_CALENDAR_COLUMNS = [
    'event_time',
    'event_tz',
    'location',
    'event_duration',
]

/**
 * What an event needs, from the `/comms/v2/pipelines/event_type` answer
 * (`{absolute_time, niceness, comm, others}`, where `comm` is a list of
 * `{<channel>: {id: [template ids], receiver_type: [...]}}`).
 *
 * @param {object} body the response body
 * @returns {{
 *   absoluteTime: boolean,
 *   niceness: boolean,
 *   channels: {commType: string, templateIds: string[], receiverTypes: string[]}[],
 *   comms: {comm_type: string, receiver_type: string}[],
 *   variables: string[],
 *   hasEmail: boolean,
 *   hasInlineReceiver: boolean,
 * }} `comms` is what requiredIdentities reads; `variables` are the
 *     template variables of every pipeline, once each
 */
export const eventDetailsFrom = (body) => {
    // The answer is a SuccessResponse with its keys at the top level; a
    // `data` wrapper is read too, in case the route moves to one.
    const source = Array.isArray(body?.comm) ? body : body?.data ?? {}
    const channels = []
    ;(Array.isArray(source.comm) ? source.comm : []).forEach((entry) => {
        Object.entries(entry ?? {}).forEach(([commType, detail]) => {
            const found = channels.find((c) => c.commType === commType)
            const target = found ?? {
                commType,
                templateIds: [],
                receiverTypes: [],
            }
            if (!found) channels.push(target)
            ;(detail?.id ?? []).forEach((id) => {
                if (id && !target.templateIds.includes(id))
                    target.templateIds.push(id)
            })
            ;(detail?.receiver_type ?? []).forEach((r) => {
                if (r && !target.receiverTypes.includes(r))
                    target.receiverTypes.push(r)
            })
        })
    })
    const comms = channels.flatMap((c) =>
        c.receiverTypes.map((r) => ({
            comm_type: c.commType,
            receiver_type: r,
        }))
    )
    const variables = [
        ...new Set(
            (Array.isArray(source.others) ? source.others : []).map(clean)
        ),
    ].filter(Boolean)
    return {
        absoluteTime: !!source.absolute_time,
        niceness: !!source.niceness,
        channels,
        comms,
        variables,
        hasEmail: channels.some((c) => c.commType === 'email'),
        hasInlineReceiver: comms.some((c) => c.receiver_type === '__inline'),
    }
}

/**
 * The template variables the file has no column for, leaving out the ones
 * filled by a link (mapped, see helpers/linkVariables). Without a column a
 * variable would go out blank.
 *
 * @param {string[]} variables the event's template variables
 * @param {{headers?: string[], mapped?: object}} file
 * @returns {string[]}
 */
export const missingVariables = (
    variables = [],
    { headers = [], mapped = {} } = {}
) => {
    const present = new Set(headers.map(clean))
    return variables.filter((v) => !mapped[v] && !present.has(v))
}

/**
 * The send's Report ID, readable like the old app's:
 * `<event_type>_<epoch seconds>_<dd>_<mon>_<yy>`, for example
 * `webinar_reminder_1792125000_16_oct_26`. It is the `event_id` of the bulk
 * log and the send, and what Reports searches by. The date is India's (the
 * old app used the browser's).
 */
export const reportIdFor = (eventType, nowSeconds) => {
    const day = inZone(nowSeconds, INDIA_ZONE)
    return `${eventType}_${nowSeconds}_${day.format('DD')}_${day
        .format('MMM')
        .toLowerCase()}_${day.format('YY')}`
}

/**
 * Recipient identities other than email and mobile, by the names the API
 * reads (CommunicationManagementService `enums.Identities`). A pipeline for
 * parents needs `lead_id` (push: `base_lead_id` and `app_name`), one for batch
 * students one of the batch ids; a CSV column with one of these names is
 * sent as that identity, as the old app does. They all went into `others`, so
 * those sends failed with "required identity not found".
 */
export const IDENTITY_COLUMNS = [
    'lead_id',
    'base_lead_id',
    'app_name',
    'batch_student_id',
    'batch_id',
    'batch_session_id',
    'user_profile_id',
    'demo_session_id',
    'demo_student_id',
]

/** The CSV headers that are identities (exact names), other than the chosen email and mobile columns. */
export const identityColumns = (
    headers = [],
    { emailColumn, mobileColumn } = {}
) =>
    headers.filter(
        (h) =>
            IDENTITY_COLUMNS.includes(clean(h)) &&
            h !== emailColumn &&
            h !== mobileColumn
    )

/**
 * Which identities the event's pipelines need, as readable groups, one per
 * rule the API checks (`__handle_identities_for_receiver_types`), for the
 * channels and receiver types in `comms`.
 *
 * @param {{comm_type: string, receiver_type: string}[]} comms
 * @returns {{label: string, anyOf: string[][]}[]} each rule passes when every
 *     name in one of its `anyOf` sets is a column
 */
export const requiredIdentities = (comms = []) => {
    const rules = []
    const add = (label, anyOf) => {
        if (!rules.some((r) => r.label === label)) rules.push({ label, anyOf })
    }
    comms.forEach(({ comm_type: commType, receiver_type: receiver }) => {
        if (receiver === 'parents') {
            if (commType === 'push_notification')
                add('base_lead_id and app_name (parents, push)', [
                    ['base_lead_id', 'app_name'],
                ])
            else add('lead_id (parents)', [['lead_id']])
        } else if (receiver === 'batch_student') {
            add(
                'batch_student_id, batch_id, batch_session_id or user_profile_id (batch students)',
                [
                    ['batch_student_id'],
                    ['batch_id'],
                    ['batch_session_id'],
                    ['user_profile_id'],
                ]
            )
        } else if (receiver === '__inline') {
            if (['whatsapp', 'sms', 'voice'].includes(commType))
                add('a mobile column', [['mobile_no']])
            else if (['email', 'slack'].includes(commType))
                add('an email column', [['email']])
        }
    })
    return rules
}

/**
 * The rules of requiredIdentities that the chosen columns do not satisfy, as
 * their labels. Email and mobile count when a column is chosen for them.
 */
export const missingIdentities = (
    rules,
    { headers = [], emailColumn, mobileColumn } = {}
) => {
    const present = new Set([
        ...identityColumns(headers, { emailColumn, mobileColumn }).map(clean),
        ...(emailColumn ? ['email'] : []),
        ...(mobileColumn ? ['mobile_no'] : []),
    ])
    return rules
        .filter((r) => !r.anyOf.some((set) => set.every((n) => present.has(n))))
        .map((r) => r.label)
}

/**
 * The CSV rows to send, de-duplicated as whole rows.
 *
 * The API pairs `identities.email[i]` with `identities.mobile_no[i]` and the
 * i-th value of every `others` column. De-duplicating the email column and
 * the mobile column separately (as this page did) shifted them against each
 * other, so a row's email went out with another row's mobile and variables.
 * Here a row is dropped only when its contact (email, else mobile, else its
 * other identities, e.g. `lead_id`) repeats an earlier row's, and rows with
 * no contact at all are skipped.
 *
 * @returns {{rows: object[], skippedEmpty: number, skippedDuplicate: number}}
 */
export const selectBulkRows = ({
    rows = [],
    emailColumn,
    mobileColumn,
    otherIdentityColumns = [],
}) => {
    const seen = new Set()
    const kept = []
    let skippedEmpty = 0
    let skippedDuplicate = 0
    rows.forEach((row) => {
        const email = emailColumn ? clean(row[emailColumn]).toLowerCase() : ''
        const mobile = mobileColumn ? clean(row[mobileColumn]) : ''
        const ids = otherIdentityColumns
            .map((c) => (clean(row[c]) ? `${c}=${clean(row[c])}` : ''))
            .filter(Boolean)
            .join('|')
        const key = email || mobile || ids
        if (!key) {
            skippedEmpty += 1
            return
        }
        if (seen.has(key)) {
            skippedDuplicate += 1
            return
        }
        seen.add(key)
        kept.push(row)
    })
    return { rows: kept, skippedEmpty, skippedDuplicate }
}

/**
 * The /comms/v2/bulk_comms body.
 *
 * A column named after an identity (IDENTITY_COLUMNS) goes into `identities`
 * under that name; every other column, apart from the email and mobile
 * columns, goes into `others`, so a template's `${first_name}` (or a WhatsApp
 * `{{first_name}}`) is filled from the column of that name. Without `others`
 * every variable rendered blank. A column named after one of the event's
 * template `variables` goes into `others` even when it is also an identity,
 * as in the old app. A variable filled by a link (`mappedOthers`) is left out
 * of `others`: the comms lambda writes the link there itself.
 *
 * @param {object} send
 * @param {string} [send.timezone] the chosen zone of a scheduled send; India
 *     for an immediate one
 * @param {number|string} [send.eventTime] epoch seconds of a scheduled send,
 *     sent as a string; '' (now) for an immediate one
 * @param {object} [send.mappedOthers] `{variable: link type}`, real mappings only
 * @param {string} [send.sessionId] the master class session id, or ''
 * @param {string[]} [send.variables] the event's template variables
 */
export const buildBulkPayload = ({
    eventType,
    eventId,
    rows,
    headers = [],
    emailColumn,
    mobileColumn,
    timezone = INDIA_ZONE,
    eventTime = '',
    mappedOthers = {},
    sessionId = '',
    variables = [],
}) => {
    const identities = {}
    if (emailColumn) identities.email = rows.map((r) => clean(r[emailColumn]))
    if (mobileColumn)
        identities.mobile_no = rows.map((r) => clean(r[mobileColumn]))
    const idColumns = identityColumns(headers, { emailColumn, mobileColumn })
    idColumns.forEach((h) => {
        identities[clean(h)] = rows.map((r) => clean(r[h]))
    })
    const others = {}
    headers
        .filter(
            (h) =>
                h &&
                !mappedOthers[clean(h)] &&
                (variables.includes(clean(h)) ||
                    (h !== emailColumn &&
                        h !== mobileColumn &&
                        !idColumns.includes(h)))
        )
        .forEach((h) => {
            others[h] = rows.map((r) => clean(r[h]))
        })
    return {
        event_type: eventType,
        event_id: eventId,
        identities,
        others,
        timezone,
        event_time:
            eventTime === '' || eventTime === null || eventTime === undefined
                ? ''
                : String(eventTime),
        mapped_others: mappedOthers,
        session_id: sessionId,
    }
}

/**
 * The /comms/v2/create_comm_bulk_log body. The send API refuses an event_id
 * with no bulk log ("comm_bulk_log not found for event_id"), so this goes
 * first, with the same id.
 *
 * @param {object} log
 * @param {number} log.eventTime epoch seconds the send goes out at (now, or
 *     the scheduled moment: the scheduled-sends list reads it)
 * @param {string[]} [log.shortLinkColumns] the columns whose links were
 *     shortened, recorded only when there are some
 */
export const buildBulkLog = ({
    eventType,
    eventId,
    eventTime,
    shortLinkColumns = [],
}) => ({
    event_id: eventId,
    event_type: eventType,
    event_time: eventTime,
    ...(shortLinkColumns.length
        ? { short_link_columns: shortLinkColumns }
        : {}),
})

/**
 * What the pre-send report should check, from the event's active pipelines
 * and the template list: the channels that would send and, for email, the
 * subject of the template (the body is fetched separately). An event with no
 * active pipeline would send nothing.
 *
 * @returns {{channels: string[], emailTemplateId: string|null, subject: string, comms: {comm_type: string, receiver_type: string}[]}}
 */
export const contentForEvent = (pipelines = [], templates = []) => {
    const comms = pipelines
        .filter((p) => !p.status || p.status === 'active')
        .flatMap((p) =>
            Array.isArray(p.comms) ? p.comms : p.comms ? [p.comms] : []
        )
    const channels = [...new Set(comms.map((c) => c.comm_type).filter(Boolean))]
    const emailComm = comms.find((c) => c.comm_type === 'email')
    const emailTemplate = emailComm
        ? templates.find((t) => t.id === emailComm.template_id)
        : null
    return {
        channels,
        emailTemplateId: emailComm?.template_id ?? null,
        subject: emailTemplate?.subject ?? '',
        // Who each comm goes to, for the identity check (requiredIdentities).
        comms: comms.map((c) => ({
            comm_type: c.comm_type,
            receiver_type: c.receiver_type,
        })),
    }
}
